import React, { useState, useEffect, useMemo } from 'react';
import { RepairJob, UserRole, JobStatus, Staff, Service, Shop, Transaction, Discount, Rating, Language } from '../types';
import { RepairJobCard } from './RepairJobCard';
import { CalendarView } from './CalendarView';
import { ShopTeam } from './ShopTeam';
import { ShopServices } from './ShopServices';
import { ShopFinance } from './ShopFinance';
import { BookingList } from './BookingList';
import { ShopDiscount } from './ShopDiscount';
import { ShopRating } from './ShopRating';
import { TrendingUp, PieChart, Calendar as CalendarIcon, Users, Store, Banknote, ListChecks, Tag, Star } from 'lucide-react';
import { formatRiel, MOCK_SHOP_ID } from '../constants';
import { useTranslation } from '../LanguageContext';

export type ShopTab = 'OVERVIEW' | 'BOOKINGS' | 'CALENDAR' | 'STAFF' | 'SERVICES' | 'FINANCE' | 'DISCOUNTS' | 'REVIEWS';

interface ShopDashboardProps {
  userRole: UserRole;
  shop?: Shop; 
  jobs: RepairJob[];
  staff: Staff[];
  services: Service[];
  transactions: Transaction[];
  discounts: Discount[];
  ratings: Rating[];
  initialTab?: ShopTab;
  onStatusChange: (id: string, newStatus: JobStatus) => void;
  onAddStaff: (newStaff: Staff) => void;
  onRemoveStaff: (staffId: string) => void;
  onAddService: (newService: Service) => void;
  onUpdateService: (updatedService: Service) => void;
  onRemoveService: (serviceId: string) => void;
  onAddTransaction: (t: Transaction) => void;
  onAddDiscount: (d: Discount) => void;
  onDeleteDiscount: (id: string) => void;
  onToggleDiscountStatus: (id: string) => void;
  onSendMessage: (jobId: string, text: string) => void;
  onNavigateToJob: (job: RepairJob) => void;
}

export const ShopDashboard: React.FC<ShopDashboardProps> = ({ 
  userRole, 
  shop,
  jobs, 
  staff,
  services,
  transactions,
  discounts,
  ratings,
  initialTab = 'OVERVIEW',
  onStatusChange, 
  onAddStaff,
  onRemoveStaff,
  onAddService,
  onUpdateService,
  onRemoveService,
  onAddTransaction,
  onAddDiscount,
  onDeleteDiscount,
  onToggleDiscountStatus,
  onNavigateToJob
}) => {
  const { language } = useTranslation();
  const [activeTab, setActiveTab] = useState<ShopTab>(initialTab);

  useEffect(() => {
    setActiveTab(initialTab);
  }, [initialTab]);

  const isOwner = userRole === UserRole.SHOP_OWNER;
  const currentShopId = shop?.id || MOCK_SHOP_ID;
  
  const shopJobs = useMemo(() => jobs.filter(j => j.shopId === currentShopId), [jobs, currentShopId]); 
  const shopServices = useMemo(() => services.filter(s => s.shopId === currentShopId), [services, currentShopId]);
  const shopStaff = useMemo(() => staff.filter(s => s.shopId === currentShopId), [staff, currentShopId]);
  const shopDiscounts = useMemo(() => discounts.filter(d => d.shopId === currentShopId), [discounts, currentShopId]);
  const shopRatings = useMemo(() => ratings.filter(r => r.shopId === currentShopId), [ratings, currentShopId]);

  const averageRating = useMemo(() => {
    if (shopRatings.length === 0) return 0;
    const sum = shopRatings.reduce((acc, r) => acc + r.stars, 0);
    return parseFloat((sum / shopRatings.length).toFixed(1));
  }, [shopRatings]);

  const totalRev = useMemo(() => {
    const jobRevenue = shopJobs
      .filter(j => j.status === JobStatus.COMPLETED && j.invoice)
      .reduce((sum, j) => sum + (j.invoice?.totalAmount || 0), 0);
    
    const manualIncome = transactions
      .filter(t => t.shopId === currentShopId && t.type === 'INCOME')
      .reduce((sum, t) => sum + t.amount, 0);
      
    return jobRevenue + manualIncome;
  }, [shopJobs, transactions, currentShopId]);

  const texts: Record<string, Record<Language, string>> = {
    title: { EN: 'Shop Workspace', KH: 'កន្លែងធ្វើការហាង', ZH: '店铺工作区', JA: 'ショップワークスペース', KO: '상점 워크스페이스' },
    sub: { EN: 'Manage your operations and growth.', KH: 'គ្រប់គ្រងប្រតិបត្តិការ និងការរីកចម្រើនរបស់អ្នក។', ZH: '管理您的运营和增长。', JA: '運営と成長を管理します。', KO: '운영 및 성장을 관리하십시오.' },
    overview: { EN: 'Overview', KH: 'ទិដ្ឋភាពទូទៅ', ZH: '概览', JA: '概要', KO: '개요' },
    bookings: { EN: 'Bookings', KH: 'ការកក់', ZH: '订单', JA: '予約', KO: '예약' },
    calendar: { EN: 'Calendar', KH: 'ប្រតិទិន', ZH: '日历', JA: 'カレンダー', KO: '캘린더' },
    reviews: { EN: 'Reviews', KH: 'ការវាយតម្លៃ', ZH: '评价', JA: 'レビュー', KO: '리뷰' },
    discounts: { EN: 'Discounts', KH: 'បញ្ចុះតម្លៃ', ZH: '折扣', JA: '割引', KO: '할인' },
    finance: { EN: 'Finance', KH: 'ហិរញ្ញវត្ថុ', ZH: '财务', JA: '財務', KO: '재무' },
    services: { EN: 'Services', KH: 'សេវាកម្ម', ZH: '服务', JA: 'サービス', KO: '服务' },
    team: { EN: 'Team', KH: 'ក្រុមការងារ', ZH: '团队', JA: 'チーム', KO: '팀' },
    pending: { EN: 'Pending', KH: 'កំពុងរង់ចាំ', ZH: '待处理', JA: '保留中', KO: '대기 중' },
    active: { EN: 'Active', KH: 'សកម្ម', ZH: '活跃', JA: '有効', KO: '활성' },
    rating: { EN: 'Rating', KH: 'កម្រិតផ្កាយ', ZH: '评分', JA: '評価', KO: '평점' },
    revenue: { EN: 'Revenue', KH: 'ចំណូល', ZH: '收入', JA: '収益', KO: '수익' },
    recent: { EN: 'Recent Activity', KH: 'សកម្មភាពថ្មីៗ', ZH: '近期活动', JA: '最近の活動', KO: '최근 활동' },
    recent_sub: { EN: 'Your latest job requests.', KH: 'សំណើការងារចុងក្រោយរបស់អ្នក។', ZH: '您的最新作业请求。', JA: '最新のジョブリクエスト。', KO: '최신 작업 요청.' },
    view_all: { EN: 'View All', KH: 'មើលទាំងអស់', ZH: '查看全部', JA: 'すべて表示', KO: '모두 보기' },
    empty: { EN: 'Your workshop is quiet.', KH: 'មិនទាន់មានសកម្មភាពនៅឡើយទេ។', ZH: '您的工作室目前很安静。', JA: '現在、ワークショップは静かです。', KO: '워크숍이 조용합니다.' }
  };

  return (
    <div className="pb-32 px-6 md:px-12 pt-24 md:pt-8 animate-in fade-in duration-500 max-w-6xl mx-auto">
      <div className="flex flex-col md:flex-row md:items-end justify-between mb-10 gap-4">
        <div>
          <h1 className="text-4xl font-black text-slate-900 tracking-tighter">{texts.title[language]}</h1>
          <p className="text-slate-500 font-bold text-lg">{texts.sub[language]}</p>
        </div>
        
        <div className="bg-slate-100 p-1.5 rounded-[1.8rem] flex flex-wrap font-black text-[10px] uppercase tracking-widest shadow-inner gap-1">
          <button onClick={() => setActiveTab('OVERVIEW')} className={`px-4 py-3 rounded-[1.4rem] transition-all flex items-center gap-2 ${activeTab === 'OVERVIEW' ? 'bg-white shadow-md text-slate-900' : 'text-slate-400 hover:text-slate-600'}`}><PieChart size={14} /> {texts.overview[language]}</button>
          <button onClick={() => setActiveTab('BOOKINGS')} className={`px-4 py-3 rounded-[1.4rem] transition-all flex items-center gap-2 ${activeTab === 'BOOKINGS' ? 'bg-white shadow-md text-slate-900' : 'text-slate-400 hover:text-slate-600'}`}><ListChecks size={14} /> {texts.bookings[language]}</button>
          <button onClick={() => setActiveTab('CALENDAR')} className={`px-4 py-3 rounded-[1.4rem] transition-all flex items-center gap-2 ${activeTab === 'CALENDAR' ? 'bg-white shadow-md text-slate-900' : 'text-slate-400 hover:text-slate-600'}`}><CalendarIcon size={14} /> {texts.calendar[language]}</button>
          {isOwner && (
            <>
              <button onClick={() => setActiveTab('REVIEWS')} className={`px-4 py-3 rounded-[1.4rem] transition-all flex items-center gap-2 ${activeTab === 'REVIEWS' ? 'bg-white shadow-md text-slate-900' : 'text-slate-400 hover:text-slate-600'}`}><Star size={14} /> {texts.reviews[language]}</button>
              <button onClick={() => setActiveTab('DISCOUNTS')} className={`px-4 py-3 rounded-[1.4rem] transition-all flex items-center gap-2 ${activeTab === 'DISCOUNTS' ? 'bg-white shadow-md text-slate-900' : 'text-slate-400 hover:text-slate-600'}`}><Tag size={14} /> {texts.discounts[language]}</button>
              <button onClick={() => setActiveTab('FINANCE')} className={`px-4 py-3 rounded-[1.4rem] transition-all flex items-center gap-2 ${activeTab === 'FINANCE' ? 'bg-white shadow-md text-slate-900' : 'text-slate-400 hover:text-slate-600'}`}><Banknote size={14} /> {texts.finance[language]}</button>
              <button onClick={() => setActiveTab('SERVICES')} className={`px-4 py-3 rounded-[1.4rem] transition-all flex items-center gap-2 ${activeTab === 'SERVICES' ? 'bg-white shadow-md text-slate-900' : 'text-slate-400 hover:text-slate-600'}`}><Store size={14} /> {texts.services[language]}</button>
              <button onClick={() => setActiveTab('STAFF')} className={`px-4 py-3 rounded-[1.4rem] transition-all flex items-center gap-2 ${activeTab === 'STAFF' ? 'bg-white shadow-md text-slate-900' : 'text-slate-400 hover:text-slate-600'}`}><Users size={14} /> {texts.team[language]}</button>
            </>
          )}
        </div>
      </div>

      {activeTab === 'OVERVIEW' && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-10">
            {[
              { label: texts.pending[language], val: shopJobs.filter(j => j.status === JobStatus.PENDING).length, color: 'text-amber-600', bg: 'bg-amber-50', border: 'border-amber-100' },
              { label: texts.active[language], val: shopJobs.filter(j => j.status === JobStatus.IN_PROGRESS).length, color: 'text-violet-600', bg: 'bg-violet-50', border: 'border-violet-100' },
              { label: texts.rating[language], val: averageRating > 0 ? averageRating : '---', sub: 'Stars', color: 'text-amber-500', bg: 'bg-white', border: 'border-slate-200', onClick: () => isOwner && setActiveTab('REVIEWS') },
              { label: texts.revenue[language], val: formatRiel(totalRev).split(' ')[0], sub: '៛', color: 'text-slate-900', bg: 'bg-white', border: 'border-slate-200', onClick: () => isOwner && setActiveTab('FINANCE') }
            ].map((stat, idx) => (
              <div key={idx} onClick={stat.onClick} className={`p-8 rounded-[2.5rem] flex flex-col items-center justify-center text-center border transition-all ${stat.bg} ${stat.border} ${stat.onClick ? 'cursor-pointer hover:shadow-xl hover:-translate-y-1' : ''}`}>
                  <span className={`text-3xl font-black mb-1 ${stat.color}`}>{stat.val}{stat.sub && <span className="text-sm ml-1 opacity-40">{stat.sub}</span>}</span>
                  <span className="text-[10px] font-black uppercase tracking-widest text-slate-400">{stat.label}</span>
              </div>
            ))}
          </div>
          <div className="bg-white rounded-[3rem] shadow-xl shadow-slate-200/50 p-10 border border-slate-100">
            <div className="flex justify-between items-center mb-10">
              <div><h2 className="text-2xl font-black text-slate-900 tracking-tight">{texts.recent[language]}</h2><p className="text-slate-400 text-sm font-bold">{texts.recent_sub[language]}</p></div>
              <button onClick={() => setActiveTab('BOOKINGS')} className="px-6 py-3 bg-slate-900 text-white rounded-2xl font-black text-xs uppercase tracking-widest hover:bg-violet-600 transition-all">{texts.view_all[language]}</button>
            </div>
            <div className="space-y-6">
              {shopJobs.length === 0 ? (
                <div className="py-20 text-center"><TrendingUp size={48} className="mx-auto text-slate-100 mb-4" /><p className="text-slate-400 font-bold italic">{texts.empty[language]}</p></div>
              ) : (
                shopJobs.slice(0, 5).map(job => <div key={job.id} onClick={() => onNavigateToJob(job)} className="cursor-pointer"><RepairJobCard job={job} role={userRole} /></div>)
              )}
            </div>
          </div>
        </>
      )}

      {activeTab === 'BOOKINGS' && <BookingList jobs={shopJobs} onNavigateToJob={onNavigateToJob} />}
      {activeTab === 'CALENDAR' && <div className="animate-in slide-in-from-bottom-4 duration-300"><CalendarView jobs={shopJobs} onSelectJob={onNavigateToJob} userRoleType="SHOP" /></div>}
      {activeTab === 'REVIEWS' && isOwner && <ShopRating ratings={shopRatings} userRole={userRole} />}
      {activeTab === 'DISCOUNTS' && isOwner && <ShopDiscount discounts={shopDiscounts} shopId={currentShopId} onAdd={onAddDiscount} onDelete={onDeleteDiscount} onToggleStatus={onToggleDiscountStatus} />}
      {activeTab === 'FINANCE' && isOwner && <ShopFinance jobs={shopJobs} transactions={transactions.filter(t => t.shopId === currentShopId)} shopId={currentShopId} onAddTransaction={onAddTransaction} />}
      {activeTab === 'STAFF' && isOwner && <ShopTeam staff={shopStaff} shopId={currentShopId} onAddStaff={onAddStaff} onRemoveStaff={onRemoveStaff} />}
      {activeTab === 'SERVICES' && <ShopServices services={shopServices} isOwner={isOwner} shopId={currentShopId} onAddService={onAddService} onUpdateService={onUpdateService} onRemoveService={onRemoveService} />}
    </div>
  );
};
